import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Activity, AlertCircle } from "lucide-react";

/**
 * BoardPortal — Read-only stewardship view served at /board?token=<jwt>.
 *
 * The digest emails to BOARD_EMAIL include a 24-hour magic link to this page
 * so board members who don't have an admin login can still tap one button and
 * see the live three-bucket dashboard (Needs thanks · Milestones · Top
 * untouched). Everything is intentionally view-only — no "Thank now" or
 * "Send to address" buttons here; those live in the authenticated admin UI.
 */
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const BoardPortal = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) {
            setError("This page requires a board access token. Please open the link from your most recent digest email.");
            return;
        }
        axios
            .get(`${API}/board/stewardship`, { params: { token } })
            .then((r) => setData(r.data))
            .catch((e) => {
                const d = e?.response?.data?.detail;
                if (Array.isArray(d)) {
                    setError(d.map((x) => x?.msg || "Invalid input").join("; "));
                } else if (typeof d === "string") {
                    setError(d);
                } else {
                    setError("Could not load board view");
                }
            });
    }, [token]);

    if (error) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-12">
                <div className="bg-white rounded-3xl border border-[#E53935]/20 shadow-sm max-w-md w-full p-8 text-center" data-testid="board-portal-error">
                    <AlertCircle className="w-10 h-10 mx-auto text-[#E53935] mb-3" strokeWidth={1.5} />
                    <h1 className="font-heading text-2xl text-[#1C252E] font-medium mb-2">Board link unavailable</h1>
                    <p className="text-sm text-[#4A5568]">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="board-portal-loading">
                <p className="text-sm text-[#4A5568]">Loading the board view…</p>
            </div>
        );
    }

    const total = (data.summary?.needs_thanks || 0) + (data.summary?.milestones || 0) + (data.summary?.top_untouched || 0);

    return (
        <div className="min-h-screen bg-[#FDFBF7] py-10 px-4" data-testid="board-portal">
            <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-2">Reddog Community Foundation</p>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">
                        Stewardship snapshot
                    </h1>
                    <p className="text-sm text-[#4A5568] mt-2">
                        Read-only board view for <strong>{data.recipient}</strong> · generated {(data.generated_at || "").slice(0, 10)} · link expires in {data.ttl_hours}h
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                    <BoardSummary label="Needs thanks" value={data.summary.needs_thanks} tone="red"
                                  sub={`Last ${data.summary.window_days} days`} testid="board-summary-needs" />
                    <BoardSummary label="Milestone crossings" value={data.summary.milestones}
                                  sub="$500 · $1k · $5k · $10k" testid="board-summary-milestones" />
                    <BoardSummary label="Top untouched" value={data.summary.top_untouched}
                                  sub="Never personally thanked" testid="board-summary-untouched" />
                </div>

                {total === 0 && (
                    <div className="bg-white border border-black/5 rounded-2xl p-10 text-center" data-testid="board-empty">
                        <Heart className="w-10 h-10 mx-auto text-[#E53935] mb-3" strokeWidth={1.5} />
                        <h2 className="font-heading text-xl text-[#1C252E] font-medium">All caught up</h2>
                        <p className="text-sm text-[#4A5568] mt-1.5">No outstanding stewardship actions right now. Check back next Monday.</p>
                    </div>
                )}

                {data.needs_thanks?.length > 0 && (
                    <BoardSection title="Recent givers needing a thank-you" subtitle={`Donors who gave in the last ${data.summary.window_days} days without a personal acknowledgment`}>
                        <ul className="divide-y divide-black/5" data-testid="board-needs-list">
                            {data.needs_thanks.map((r) => (
                                <li key={r.email} className="py-3" data-testid={`board-needs-row-${r.email}`}>
                                    <p className="font-medium text-[#1C252E]">{r.donor_name || r.email}</p>
                                    <p className="text-xs text-[#4A5568]">
                                        ${Number(r.last_amount).toFixed(2)} on {(r.last_gift_at || "").slice(0, 10)} · lifetime ${Number(r.lifetime).toFixed(2)} · {r.reason}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </BoardSection>
                )}

                {data.milestones?.length > 0 && (
                    <BoardSection title="Milestone crossings" subtitle="Donors who just crossed a lifetime tier">
                        <ul className="divide-y divide-black/5" data-testid="board-milestones-list">
                            {data.milestones.map((r) => (
                                <li key={r.email} className="py-3" data-testid={`board-milestone-row-${r.email}`}>
                                    <p className="font-medium text-[#1C252E]">
                                        {r.donor_name || r.email}{" "}
                                        <span className="text-xs ml-1 px-2 py-0.5 rounded-full bg-[#F2D8D5] text-[#E53935]">
                                            ${Math.round(r.tier).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-xs text-[#4A5568]">Lifetime ${Number(r.lifetime).toFixed(2)} · {r.gift_count} gift(s)</p>
                                </li>
                            ))}
                        </ul>
                    </BoardSection>
                )}

                {data.top_untouched?.length > 0 && (
                    <BoardSection title="Top untouched donors" subtitle="Top 10 lifetime givers who have never received a personal thank-you">
                        <ul className="divide-y divide-black/5" data-testid="board-untouched-list">
                            {data.top_untouched.map((r, idx) => (
                                <li key={r.email} className="py-3 flex items-center gap-3" data-testid={`board-untouched-row-${r.email}`}>
                                    <span className="text-xs font-bold text-[#4A5568] w-5 text-right">#{idx + 1}</span>
                                    <div>
                                        <p className="font-medium text-[#1C252E]">{r.donor_name || r.email}</p>
                                        <p className="text-xs text-[#4A5568]">
                                            Lifetime ${Number(r.lifetime).toFixed(2)} · {r.gift_count} gift(s)
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </BoardSection>
                )}

                <div className="mt-10 text-center" data-testid="board-portal-footer">
                    <p className="text-xs text-[#4A5568]">
                        <Activity className="w-3.5 h-3.5 inline mb-0.5 mr-1" strokeWidth={1.5} />
                        Read-only board view · Reddog Community Foundation Inc. · 501(c)(3) recognition pending
                    </p>
                </div>
            </div>
        </div>
    );
};

const BoardSummary = ({ label, value, sub, tone, testid }) => {
    const bg = tone === "red" ? "bg-[#F2D8D5]" : "bg-white border border-black/5";
    const accent = tone === "red" ? "text-[#E53935]" : "text-[#1C252E]";
    return (
        <div className={`${bg} rounded-2xl p-5`} data-testid={testid}>
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568] mb-1.5">{label}</p>
            <p className={`font-heading text-4xl ${accent} font-medium`}>{value}</p>
            <p className="text-xs text-[#4A5568] mt-1.5">{sub}</p>
        </div>
    );
};

const BoardSection = ({ title, subtitle, children }) => (
    <div className="bg-white border border-black/5 rounded-2xl p-5 mb-4">
        <h2 className="font-heading text-lg text-[#1C252E] font-medium">{title}</h2>
        <p className="text-xs text-[#4A5568] mt-0.5 mb-2">{subtitle}</p>
        {children}
    </div>
);

export default BoardPortal;
